// @ts-nocheck
import React, { useState } from 'react';
import { ImageIcon, SparklesIcon, TrashIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { getSlideIntervalMs, reorderSlideshowAttachments } from '../utils/slideshowUtils';
import { handleSliderPointerDown } from '../utils/helpers';

export const isVideoUrl = (url: string) => {
    return url.match(/\.(mp4|webm|ogg)$/i) || (url.startsWith('blob:') && url.includes('#video'));
};

interface SlideshowBuilderProps {
    slideshow: any;
    onChangeSlideshow: (s: any) => void;
    nativeInputRef: React.RefObject<HTMLInputElement>;
    onNativeUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onOpenTenor?: () => void;
    onOpenPhotos?: (provider: 'apple' | 'google') => void;
    isAdmin?: boolean;
}

export const SlideshowBuilder: React.FC<SlideshowBuilderProps> = ({
    slideshow,
    onChangeSlideshow,
    nativeInputRef,
    onNativeUpload,
    onOpenTenor,
    onOpenPhotos,
    isAdmin
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const updateAttachment = (index: number, updates: Record<string, any>) => {
        const list = [...(slideshow.attachments || [])];
        list[index] = { ...list[index], ...updates };
        onChangeSlideshow({ ...slideshow, attachments: list });
    };

    const handleDrop = (targetIndex: number) => {
        if (draggedIndex === null || draggedIndex === targetIndex) {
            setDragOverIndex(null);
            setDraggedIndex(null);
            return;
        }
        const list = reorderSlideshowAttachments(slideshow.attachments || [], draggedIndex, targetIndex);
        onChangeSlideshow({ ...slideshow, attachments: list });
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDelete = (index: number) => {
        const list = [...(slideshow.attachments || [])];
        list.splice(index, 1);
        onChangeSlideshow({ ...slideshow, attachments: list });
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="space-y-4 animate-fade-in">
                {/* Attachments List */}
                <div className="space-y-2">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-white/60" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Slides ({slideshow.attachments?.length || 0})</label>
                    
                    {(!slideshow.attachments || slideshow.attachments.length === 0) ? (
                        <div className="p-6 text-center bg-black/10 border border-dashed border-white/10 rounded-2xl">
                            <p className="text-[10px] text-white/40 uppercase tracking-widest">No backgrounds added yet</p>
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                            {slideshow.attachments.map((item: any, idx: number) => {
                                const isDraggingThis = draggedIndex === idx;
                                const isTargetHover = dragOverIndex === idx && draggedIndex !== null && draggedIndex !== idx;

                                return (
                                <div
                                    key={item.id || idx}
                                    draggable
                                    onDragStart={() => setDraggedIndex(idx)}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        if (draggedIndex !== null && draggedIndex !== idx && dragOverIndex !== idx) {
                                            setDragOverIndex(idx);
                                        }
                                    }}
                                    onDragLeave={() => {
                                        if (dragOverIndex === idx) setDragOverIndex(null);
                                    }}
                                    onDrop={() => handleDrop(idx)}
                                    onDragEnd={() => {
                                        setDraggedIndex(null);
                                        setDragOverIndex(null);
                                    }}
                                    className={`flex flex-col gap-2 p-2 rounded-xl transition-all ${
                                        isDraggingThis
                                            ? 'opacity-40 bg-black/40 border border-white/10 scale-[0.98]'
                                            : isTargetHover
                                            ? 'bg-blue-500/20 border-2 border-blue-400 shadow-lg shadow-blue-500/20 scale-[1.01]'
                                            : 'bg-black/20 border border-white/5'
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-lg overflow-hidden border border-white/10 shrink-0 bg-black/40">
                                                {item.type === 'video' || isVideoUrl(item.url) ? (
                                                    <video src={item.url} className="w-full h-full object-cover" muted playsInline />
                                                ) : (
                                                    <img src={item.url} className="w-full h-full object-cover" alt="" />
                                                )}
                                            </div>
                                            <span className="text-[10px] font-bold truncate text-white/80">{item.name || `Background Slide #${idx + 1}`}</span>
                                        </div>

                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <div className="p-1 rounded-lg bg-white/5 text-white/60 cursor-grab active:cursor-grabbing">
                                                <span className="text-[10px] block leading-none">⋮⋮</span>
                                            </div>
                                            <button
                                                onClick={() => handleDelete(idx)}
                                                className="p-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2 pt-1 border-t border-white/5">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[9px] font-bold uppercase tracking-tight text-white/60">Fill / Tile</span>
                                                <div className="flex bg-black/40 p-0.5 rounded-xl border border-white/5">
                                                    {(['cover', 'tile'] as const).map(style => {
                                                        const currentStyle = item.style || 'tile';
                                                        return (
                                                            <button
                                                                key={style}
                                                                type="button"
                                                                onClick={() => updateAttachment(idx, { style })}
                                                                className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase transition-all ${currentStyle === style ? 'bg-white/20 text-white' : 'text-white/70'}`}
                                                            >
                                                                {style === 'cover' ? 'Fill' : 'Tile'}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <span className="text-[9px] font-bold uppercase tracking-tight text-white/60">Timer</span>
                                                <div className="flex items-center bg-black/40 border border-white/10 rounded-xl overflow-hidden p-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const current = item.durationValue || 10;
                                                            updateAttachment(idx, { durationValue: Math.max(1, current - 1), durationUnit: 'seconds' });
                                                        }}
                                                        className="w-5 h-5 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-xs font-bold transition-all active:scale-95 cursor-pointer touch-manipulation"
                                                        title="Decrease timer"
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.durationValue || 10}
                                                        onChange={(e) => updateAttachment(idx, { durationValue: Math.max(1, Number(e.target.value)), durationUnit: 'seconds' })}
                                                        className="w-8 bg-transparent text-center text-[10px] font-bold text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const current = item.durationValue || 10;
                                                            updateAttachment(idx, { durationValue: current + 1, durationUnit: 'seconds' });
                                                        }}
                                                        className="w-5 h-5 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-xs font-bold transition-all active:scale-95 cursor-pointer touch-manipulation"
                                                        title="Increase timer"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <span className="text-[9px] font-bold text-white/50 uppercase">sec</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 w-full pt-1">
                                            <span className="text-[9px] font-bold uppercase tracking-tight text-white/60 shrink-0">Tile Resolution</span>
                                            <input
                                                type="range"
                                                min="25"
                                                max="200"
                                                step="1"
                                                value={item.resolution || 100}
                                                onPointerDown={(e) => handleSliderPointerDown(e, item.resolution || 100, 25, 200)}
                                                onChange={(e) => updateAttachment(idx, { resolution: Number(e.target.value) })}
                                                className="flex-1 h-1.5 bg-black/40 rounded-lg appearance-none cursor-pointer accent-white/80"
                                            />
                                            <div className="flex items-center bg-black/40 border border-white/10 rounded-xl overflow-hidden p-0.5 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const current = item.resolution || 100;
                                                        updateAttachment(idx, { resolution: Math.max(25, current - 1) });
                                                    }}
                                                    className="w-5 h-5 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-xs font-bold transition-all active:scale-95 cursor-pointer touch-manipulation"
                                                    title="Decrease resolution"
                                                >
                                                    -
                                                </button>
                                                <span className="w-9 text-center text-[10px] font-bold text-white/90">
                                                    {item.resolution || 100}%
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const current = item.resolution || 100;
                                                        updateAttachment(idx, { resolution: Math.min(200, current + 1) });
                                                    }}
                                                    className="w-5 h-5 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-xs font-bold transition-all active:scale-95 cursor-pointer touch-manipulation"
                                                    title="Increase resolution"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                            })}
                        </div>
                    )}
                </div>

                {/* Add Media Options */}
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={(e) => {
                            console.log('[STEP 1] Upload Files button clicked');
                            if (nativeInputRef.current) {
                                console.log('[STEP 2] Programmatically clicking nativeInputRef input');
                                nativeInputRef.current.click();
                            } else {
                                console.error('[STEP 2 ERROR] nativeInputRef.current is NULL!');
                            }
                        }}
                        className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                    >
                        <ImageIcon className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" />
                        <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Upload Files</span>
                    </button>

                    {onOpenTenor && (
                        <button
                            type="button"
                            onClick={onOpenTenor}
                            className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                        >
                            <SparklesIcon className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" />
                            <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Tenor GIF</span>
                        </button>
                    )}

                    {isAdmin && onOpenPhotos && (
                        <>
                            <button
                                onClick={() => onOpenPhotos('apple')}
                                className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                            >
                                <span className="text-base text-gray-300 font-bold group-hover:scale-110 transition-transform">?</span>
                                <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Apple Photos</span>
                            </button>

                            <button
                                onClick={() => onOpenPhotos('google')}
                                className="flex flex-col items-center justify-center p-3 bg-black/20 hover:bg-black/40 border border-white/5 rounded-2xl transition-all gap-1.5 group"
                            >
                                <span className="text-xs text-yellow-500 font-bold group-hover:scale-110 transition-transform">G</span>
                                <span className="text-[9px] font-bold uppercase tracking-tight text-white/70">Google Photos</span>
                            </button>
                        </>
                    )}
                </div>

                {/* Hidden Native File Input */}
                <input
                    type="file"
                    ref={nativeInputRef}
                    onChange={(e) => {
                        console.log('[STEP 3] Hidden input onChange fired! Files count:', e.target.files?.length);
                        if (onNativeUpload) {
                            onNativeUpload(e);
                        }
                    }}
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                />

            </div>
        </div>
    );
};
